<script>
import EgStepper from "@/components/global/eg_stepper.vue";
import subscriptionCancelConfirmModal from "./subscription_cancel_confirm_modal.vue";
import subscriptionCancelSwitchPlan from "./subscription_cancel_switch_plan.vue";
import cancellingReasonModal from "./cancelling_reason_modal.vue";
import subscriptionCancelUpgradeModal from "./subscription_cancel_upgrade_modal.vue";
import subscriptionCancelFinalStep from "./subscription_cancel_final_step.vue";
import PauseSubscriptionModal from "./pause_subscription_modal.vue";

export default {
  name: "CancelSubscriptionModal",

  components: {
    subscriptionCancelConfirmModal,
    subscriptionCancelUpgradeModal,
    subscriptionCancelSwitchPlan,
    cancellingReasonModal,
    subscriptionCancelFinalStep,
    PauseSubscriptionModal,

    EgStepper,
  },

  props: {
    pauseSubscription: {
      type: Function,
      required: true,
    },
    switchPlan: {
      type: Function,
      required: true,
    },
    cancelSubscription: {
      type: Function,
      required: true,
    },
    username: {
      type: String,
      required: true,
    },
    applyCoupon: {
      type: Function,
      required: true,
    },
    isLitePlan: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    parentContext: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      activeStepIndex: 0,
      steperLoading: false,
      cancellingReason: {},
      isConfirm: true,
      allSteps: [
        {
          title: "Are you sure you want to cancel your plan?",
          component: "subscriptionCancelConfirmModal",
          actions: [
            {
              label: "Cancel subscription",
              action: this.doNextStep,
              isNextStepHandler: true,
              type: "secondary",
            },
            {
              label: "Pause Subscription",
              action: this.openModal,
              type: "primary",
            },
          ],
        },
        {
          title: "Would you like to switch to our Lite Plan?",
          component: "subscriptionCancelSwitchPlan",
          actions: [
            {
              label: "Cancel subscription",
              action: this.doNextStep,
              isNextStepHandler: true,
              type: "ordinary",
            },
            {
              label: "Switch plan",
              loadingText: "Switching...",
              action: this.switchPlan,
              type: "primary",
            },
          ],
        },
        {
          title: "We are sorry to see you go.",
          component: "cancellingReasonModal",
          actions: [
            {
              label: "Submit",
              action: this.doNextStep,
              isNextStepHandler: true,
              id: "subscriptionCancelModal_submit",
              type: "primary",
            },
          ],
        },
        {
          title: "We don't want to see you go!",
          component: "subscriptionCancelUpgradeModal",
          actions: [
            {
              label: "Cancel subscription",
              action: this.doNextStep,
              isNextStepHandler: true,
              type: "secondary",
            },
            {
              label: "Yes",
              action: this.applyCoupon,
              type: "primary",
            },
          ],
        },
        {
          title: "Cancel subscription",
          component: "subscriptionCancelFinalStep",
          actions: [
            {
              label: "Submit",
              action: this.doNextStep,
              isNextStepHandler: true,
              loadingText: "Submitting...",
              id: "subscriptionCancelFinalStep_submit",
              type: "primary",
            },
          ],
        },
      ],
    };
  },

  computed: {
    isPaused() {
      return this.$store.getters.isPaused;
    },

    isYearlyPlan() {
      // Check if current plan is yearly based on payment interval
      return this.parentContext?.accountProvider?.paymentInterval === "year";
    },

    steps() {
      // If user has Lite plan, skip the "switch to Lite Plan" step (index 1)
      if (this.isLitePlan) {
        return this.allSteps.filter((_, index) => index !== 1);
      }
      return this.allSteps;
    },
  },

  methods: {
    beforeChange(step) {
      step.resolve();
    },

    async finishHandler(resolve) {
      await this.cancelSubscription(this.cancellingReason);
      resolve();
    },

    async doNextStep(goNext) {
      await goNext();
    },

    hideModal() {
      this.activeStepIndex = 0;
      this.steperLoading = false;
    },

    async openModal() {
      await this.$egModal.hide("cancel_subscription_modal");
      await this.$nextTick();
      this.$egModal.show("pause_subscription_modal", {
        component: PauseSubscriptionModal,
        context: this.parentContext || this,
        propsKeys: ["pauseSubscription", "loading"],
      });
    },

    isDisabledBtn(action) {
      // Disable pause subscription button when subscription is already paused
      if (action.label === "Pause Subscription" && this.isPaused) {
        return true;
      }

      switch (action.id) {
        case "subscriptionCancelModal_submit":
          return !Object.keys(this.cancellingReason).length;
        case "subscriptionCancelFinalStep_submit":
          return !this.isConfirm;

        default:
          return false;
      }
    },

    shouldShowAction(action) {
      // Hide pause subscription button for yearly plans
      if (action.label === "Pause Subscription" && this.isYearlyPlan) {
        return false;
      }
      return true;
    },
  },
};
</script>

<template>
  <eg-modal
    id="cancel_subscription_modal"
    :title="steps[activeStepIndex].title"
    centered
    no-stacking
    hide-footer
    @hide="hideModal"
  >
    <eg-stepper
      v-model="steperLoading"
      finish-label="Finish"
      :border-bottom="false"
      @beforeChange="beforeChange"
      @change="activeStepIndex = $event"
      @finish="finishHandler"
    >
      <template v-for="(step, index) in steps" :slot="'slot-' + (index + 1)">
        <component
          :is="step.component"
          :key="index"
          :cancel-subscription="cancelSubscription"
          :pause-subscription="pauseSubscription"
          :loading="loading"
          :username="username"
          :apply-coupon="applyCoupon"
          :is-lite-plan="isLitePlan"
          @reasonSelected="cancellingReason = $event"
          @isConfirm="isConfirm = $event"
        />
      </template>

      <template #actions="{ currentStepIndex, goNext }">
        <div class="d-flex justify-content-end stepper-actions">
          <eg-button
            v-for="(action, index) in steps[currentStepIndex].actions"
            v-show="shouldShowAction(action)"
            :key="index"
            :loading-text="action.loadingText"
            :type="action.type"
            :is-loading="loading"
            :disabled="isDisabledBtn(action)"
            class="ml-3"
            @click="
              () => action.action(action.isNextStepHandler ? goNext : undefined)
            "
          >
            {{ action.label }}
          </eg-button>
        </div>
      </template>
    </eg-stepper>
  </eg-modal>
</template>

<style lang="scss">
#cancel_subscription_modal {
  font-family: Inter;

  .eg-modal__body {
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 0;
  }

  .modal-body {
    padding: 0 !important;
  }

  .steps-body {
    padding: 0 !important;
  }

  .step-block {
    padding: 16px !important;
    border-bottom: 1px solid #dee2e6;

    .content {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;
      border-bottom: 1px solid #dee2e6;
    }

    .text {
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0px;
      vertical-align: bottom;
    }

    .bold-text {
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0px;
      vertical-align: bottom;
    }
  }

  .img-content {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    border-bottom: 1px solid #dee2e6;
  }

  .stepper-actions {
    padding: 16px !important;
  }

  .steps-progress {
    margin-bottom: 0px !important;
    border-bottom: 1px solid #dee2e6;
  }

  .modal-content {
    max-width: 500px;
  }

  .modal-header {
    border-bottom: none !important;
  }

  .modal-title {
    font-family: Inter;
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: 0px;
    vertical-align: bottom;
  }
}
</style>
